import { useState, useEffect, useRef, useCallback, memo } from "react";
import SearchBar from "./SearchBar";
import HistoryPanel from "./HistoryPanel";
import SettingsPanel from "./SettingsPanel";
import { SunIcon, MoonIcon } from "./icons";
import { ExplanationEntry, Flashcard, SearchHistoryItem } from "../types";
import { searchExplanation } from "../services/search";
import { getSelectedModelId } from "../services/models";
import logo from "../assets/logo.svg";

const StreamedText = memo(function StreamedText({
  explanation,
  onStreamComplete,
}: {
  explanation: string;
  onStreamComplete: () => void;
}) {
  const [streamedText, setStreamedText] = useState<string>("");
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const streamIntervalRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fullTextRef = useRef<HTMLDivElement>(null);

  const isStreamingEnabled =
    localStorage.getItem("enableStreamingText") !== "false";

  useEffect(() => {
    if (explanation.length > 0 && fullTextRef.current && containerRef.current) {
      fullTextRef.current.textContent = explanation;
      const fullHeight = fullTextRef.current.getBoundingClientRect().height;
      containerRef.current.style.minHeight = `${fullHeight}px`;
    }

    if (explanation.length === 0) return;

    if (!isStreamingEnabled) {
      setStreamedText(explanation);
      setIsStreaming(false);
      onStreamComplete();
      return;
    }

    setIsStreaming(true);
    setStreamedText("");
    let index = 0;

    streamIntervalRef.current = window.setInterval(() => {
      if (index < explanation.length) {
        const currentChar = explanation.charAt(index);
        setStreamedText((prev) => prev + currentChar);
        index++;
      } else {
        if (streamIntervalRef.current !== null) {
          clearInterval(streamIntervalRef.current);
          streamIntervalRef.current = null;
        }
        setIsStreaming(false);
        onStreamComplete();
      }
    }, 10);

    return () => {
      if (streamIntervalRef.current !== null) {
        clearInterval(streamIntervalRef.current);
        streamIntervalRef.current = null;
      }
    };
  }, [explanation, onStreamComplete, isStreamingEnabled]);

  return (
    <div ref={containerRef} className="relative">
      <div className="relative z-10">
        {streamedText}
        {isStreaming && <span className="animate-pulse">|</span>}
      </div>

      <div
        ref={fullTextRef}
        className="absolute opacity-0 pointer-events-none"
        aria-hidden="true"
      >
        {explanation}
      </div>
    </div>
  );
});

type TabType = "search" | "history" | "settings";

const DarkModeIcon: React.FC<{ darkMode: boolean }> = ({ darkMode }) =>
  darkMode ? (
    <SunIcon className="h-5 w-5" />
  ) : (
    <MoonIcon className="h-5 w-5" />
  );

interface NavTabProps {
  id: TabType;
  label: string;
  activeTab: TabType;
  onClick: (tab: TabType) => void;
}

const NavTab: React.FC<NavTabProps> = ({ id, label, activeTab, onClick }) => {
  return (
    <button
      onClick={() => onClick(id)}
      className={`py-2 px-4 text-sm font-medium transition-colors ${
        activeTab === id
          ? "border-b-2 border-blue-500 text-blue-500 dark:text-blue-400 dark:border-blue-400"
          : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
      }`}
      aria-label={label}
    >
      {label}
    </button>
  );
};

const Redefine: React.FC = () => {
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    const savedMode = localStorage.getItem("darkMode");
   if (savedMode !== null) {
      const parsedMode = JSON.parse(savedMode);
      return parsedMode;
    }

    const prefersDarkMode = window.matchMedia(
      "(prefers-color-scheme: dark)"
    ).matches;
    console.log(
      "No saved preference found, using system preference:",
      prefersDarkMode
    );
    return prefersDarkMode;
  });

  const [activeTab, setActiveTab] = useState<TabType>(() => {
    const savedTab = localStorage.getItem("activeTab");
    return savedTab ? (JSON.parse(savedTab) as TabType) : "search";
  });

  const [query, setQuery] = useState<string>("");
  const [wordData, setWordData] = useState<
    ExplanationEntry | { query: string; error: boolean } | null
  >(null);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);

  const [exportedFlashcards, setExportedFlashcards] = useState<Flashcard[]>(
    () => {
      const exported = localStorage.getItem("exportedFlashcards");
      return exported ? JSON.parse(exported) : [];
    }
  );
  const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>(
    () => {
      const history = localStorage.getItem("searchHistory");
      return history ? JSON.parse(history) : [];
    }
  );
  const [notification, setNotification] = useState<{
    message: string;
    type: "success" | "error";
    visible: boolean;
  } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingFlashcards, setIsLoadingFlashcards] =
    useState<boolean>(false);
  const [showModelRequiredMessage, setShowModelRequiredMessage] =
    useState<boolean>(false);

  const [explanationText, setExplanationText] = useState<string>("");

  const preventSuggestionsRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem(
      "exportedFlashcards",
      JSON.stringify(exportedFlashcards)
    );
  }, [exportedFlashcards]);

  useEffect(() => {
    localStorage.setItem("searchHistory", JSON.stringify(searchHistory));
  }, [searchHistory]);

  useEffect(() => {
    localStorage.setItem("activeTab", JSON.stringify(activeTab));
  }, [activeTab]);

  const toggleDarkMode = (): void => {
    setDarkMode((prev) => !prev);
  };

  const handleStreamComplete = useCallback(() => {
    setIsStreaming(false);
  }, []);

  const handleSearch = async (searchQuery: string): Promise<void> => {
    if (!searchQuery) return;

    const isModelConfigured = (): boolean => {
      const selectedModel = localStorage.getItem("selectedModel");
      return !!selectedModel;
    };

    if (!isModelConfigured()) {
      setShowModelRequiredMessage(true);
      return;
    }

    setQuery(searchQuery);
    setIsStreaming(true);
    setExplanationText("");

    setIsLoading(true);
    setIsLoadingFlashcards(true);
    setWordData(null);

    try {
      const modelId = getSelectedModelId();
      if (!modelId) {
        setShowModelRequiredMessage(true);
        setIsLoading(false);
        setIsLoadingFlashcards(false);
        return;
      }

      const generalPromise = searchExplanation(searchQuery, modelId, "general");
      const ankiPromise = searchExplanation(searchQuery, modelId, "anki");

      setSearchHistory((prev) => {
        const filteredHistory = prev.filter(
          (item) => item.query !== searchQuery
        );
        return [
          { query: searchQuery, timestamp: new Date().toISOString() },
          ...filteredHistory,
        ].slice(0, 100);
      });

      let generalDataReceived = false;
      let ankiDataReceived = false;
      let generalData: ExplanationEntry | null = null;
      let ankiFlashcards:
        | Array<{ type: string; front: string; back: string }>
        | undefined = undefined;

      generalPromise
        .then((data) => {
          generalData = data;
          generalDataReceived = true;

          setWordData({
            ...data,
            flashcards: ankiDataReceived ? ankiFlashcards || [] : [],
          });

          setExplanationText(data.explanation);
          setIsLoading(false);

          if (ankiDataReceived) {
            setIsLoadingFlashcards(false);
          }
        })
        .catch((error) => {
          console.error("Error fetching general explanation:", error);
          if (!ankiDataReceived) {
            setWordData({
              query: searchQuery,
              error: true,
            });
            setExplanationText("");
            setNotification({
              message:
                "Failed to retrieve explanation. Please try again later.",
              type: "error",
              visible: true,
            });
          }
          setIsLoading(false);

          if (ankiDataReceived) {
            setIsLoadingFlashcards(false);
          }
        });

      ankiPromise
        .then((data) => {
          ankiFlashcards = data.flashcards;
          ankiDataReceived = true;

          if (generalDataReceived && generalData) {
            setWordData((currentWordData) => {
              if (currentWordData && !("error" in currentWordData)) {
                return {
                  ...currentWordData,
                  flashcards: data.flashcards || [],
                };
              }
              return currentWordData;
            });
          }

          if (generalDataReceived) {
            setIsLoadingFlashcards(false);
          }
        })
        .catch((error) => {
          console.error("Error fetching Anki flashcards:", error);
          ankiDataReceived = true;

          if (generalDataReceived) {
            setIsLoadingFlashcards(false);
          }
        });

      await Promise.allSettled([generalPromise, ankiPromise]);

      if (isLoading) setIsLoading(false);
      if (isLoadingFlashcards) setIsLoadingFlashcards(false);
    } catch (error) {
      console.error("Unexpected error in search handling:", error);
      setWordData({
        query: searchQuery,
        error: true,
      });
      setExplanationText("");
      setNotification({
        message: "An unexpected error occurred. Please try again.",
        type: "error",
        visible: true,
      });
      setIsLoading(false);
      setIsLoadingFlashcards(false);
    }
  };

  const handleNavigateToSearch = (word: string): void => {
    setActiveTab("search");
    if (preventSuggestionsRef.current) {
      preventSuggestionsRef.current();
    }
    handleSearch(word);
  };

  const setPreventSuggestionsRef = useCallback((fn: () => void) => {
    preventSuggestionsRef.current = fn;
  }, []);

  const handleNavigateToSettings = () => {
    setActiveTab("settings");
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <div className="w-full max-w-4xl mx-auto flex-1 p-4 md:py-12 md:px-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 md:p-8 content-container">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              <img
                src={logo}
                alt="Redefine logo"
                className={`h-8 md:h-10 mr-3 ${
                  darkMode ? "logo-dark-mode" : ""
                }`}
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                  Redefine
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  AI-powered word learning reimagined.
                </p>
              </div>
            </div>
            <button
              onClick={toggleDarkMode}
              className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              aria-label={
                darkMode ? "Switch to light mode" : "Switch to dark mode"
              }
            >
              <DarkModeIcon darkMode={darkMode} />
            </button>
          </div>

          <div className="mb-6">
            <nav className="flex border-b border-gray-200 dark:border-gray-700">
              <NavTab
                id="search"
                label="Search"
                activeTab={activeTab}
                onClick={setActiveTab}
              />
              <NavTab
                id="history"
                label="History & Flashcards"
                activeTab={activeTab}
                onClick={setActiveTab}
              />
              <NavTab
                id="settings"
                label="Settings"
                activeTab={activeTab}
                onClick={setActiveTab}
              />
            </nav>
          </div>

          <div style={{ display: activeTab === "search" ? "block" : "none" }}>
            <SearchBar
              query={query}
              setQuery={setQuery}
              wordData={wordData}
              setWordData={setWordData}
              isStreaming={isStreaming}
              setIsStreaming={setIsStreaming}
              streamedTextComponent={
                wordData && !("error" in wordData) ? (
                  <StreamedText
                    explanation={explanationText}
                    onStreamComplete={handleStreamComplete}
                  />
                ) : null
              }
              exportedFlashcards={exportedFlashcards}
              setExportedFlashcards={setExportedFlashcards}
              onNavigateToSettings={handleNavigateToSettings}
              handleSearch={handleSearch}
              isLoading={isLoading}
              isLoadingFlashcards={isLoadingFlashcards}
              notification={notification}
              setNotification={setNotification}
              showModelRequiredMessage={showModelRequiredMessage}
              setShowModelRequiredMessage={setShowModelRequiredMessage}
              activeTab={activeTab}
              setPreventSuggestionsRef={setPreventSuggestionsRef}
            />
          </div>
          <div style={{ display: activeTab === "history" ? "block" : "none" }}>
            <HistoryPanel
              searchHistory={searchHistory}
              setSearchHistory={setSearchHistory}
              exportedFlashcards={exportedFlashcards}
              setExportedFlashcards={setExportedFlashcards}
              onNavigateToSearch={handleNavigateToSearch}
            />
          </div>
          <div style={{ display: activeTab === "settings" ? "block" : "none" }}>
            <SettingsPanel />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Redefine;
